import React, { useState, useEffect, useCallback } from 'react';
import { aiService } from '../../services/api';
import { FiPlus, FiEdit2, FiTrash2, FiCheck, FiX, FiRefreshCw, FiClock, FiCode, FiServer, FiSave, FiList, FiFileText, FiLoader, FiSettings, FiKey, FiZap } from 'react-icons/fi';
import Loading from '../common/Loading';

const PROVIDER_TYPES = [
  { value: 'openai', label: 'OpenAI' },
  { value: 'gemini', label: 'Google Gemini' },
  { value: 'ollama', label: 'Ollama (local)' },
  { value: 'openrouter', label: 'OpenRouter' },
  { value: 'nvidia', label: 'NVIDIA' },
  { value: 'local', label: 'Local LLM' },
  { value: 'bluesminds', label: 'Bluesminds' },
  { value: 'custom', label: 'Custom (OpenAI-compatível)' }
];

const DEFAULT_BASE_URLS = {
  openai: 'https://api.openai.com/v1/',
  gemini: 'https://generativelanguage.googleapis.com/v1beta/openai/',
  ollama: 'http://localhost:11434',
  openrouter: 'https://openrouter.ai/api/v1/',
  nvidia: 'https://integrate.api.nvidia.com/v1/',
  local: 'http://localhost:8080/v1/',
  bluesminds: 'https://api.bluesminds.com/v1/responses',
  custom: ''
};

const DEFAULT_MODELS = {
  openai: 'gpt-4',
  gemini: 'gemini-2.5-flash',
  ollama: 'llama3',
  openrouter: 'openai/gpt-4',
  nvidia: 'meta/llama3-70b-instruct',
  local: '',
  bluesminds: 'gpt-5.5',
  custom: ''
};

function IA() {
  const [activeTab, setActiveTab] = useState('providers');
  const [providers, setProviders] = useState([]);
  const [actionLog, setActionLog] = useState([]);
  const [logPagination, setLogPagination] = useState({ total: 0, page: 1, total_pages: 1 });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);

  const [showProviderModal, setShowProviderModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteType, setDeleteType] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [editingProvider, setEditingProvider] = useState(null);
  const [providerForm, setProviderForm] = useState({
    name: '', provider_type: 'openai', base_url: '', api_key: '',
    default_model: '', max_tokens: 4096, temperature: 0.7,
    is_active: 1, is_default: 0, sort_order: 0
  });
  const [availableModels, setAvailableModels] = useState([]);
  const [providerModels, setProviderModels] = useState([]);
  const [providerModelsLoading, setProviderModelsLoading] = useState(false);
  const [showModelInput, setShowModelInput] = useState(false);
  const [providerModelFilter, setProviderModelFilter] = useState('');
  const [modelsProviderId, setModelsProviderId] = useState(null);
  const [loadingModels, setLoadingModels] = useState(false);

  const [testingProviders, setTestingProviders] = useState({});
  const [logDetail, setLogDetail] = useState(null);
  const [undoing, setUndoing] = useState(null);
  const [systemPrompt, setSystemPrompt] = useState('');
  const [systemPromptSaving, setSystemPromptSaving] = useState(false);
  const [systemPromptLoading, setSystemPromptLoading] = useState(false);

  const [ocrSpaceKey, setOcrSpaceKey] = useState('');
  const [ocrSpaceKeyLoading, setOcrSpaceKeyLoading] = useState(false);
  const [ocrSpaceKeySaving, setOcrSpaceKeySaving] = useState(false);

  const [promptTemplates, setPromptTemplates] = useState([]);
  const [promptTemplatesLoading, setPromptTemplatesLoading] = useState(false);
  const [showPromptTemplateModal, setShowPromptTemplateModal] = useState(false);
  const [editingPromptTemplate, setEditingPromptTemplate] = useState(null);
  const [promptTemplateForm, setPromptTemplateForm] = useState({
    title: '', prompt_text: '', category: '', sort_order: 0, is_active: 1
  });
  const [promptTemplateSaving, setPromptTemplateSaving] = useState(false);

  const [functions, setFunctions] = useState({});
  const [functionsLoading, setFunctionsLoading] = useState(false);
  const [functionsTotal, setFunctionsTotal] = useState(0);

  const showMessage = useCallback((type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 4000);
  }, []);

  const loadProviders = useCallback(async () => {
    try {
      setLoading(true);
      const response = await aiService.getProviders();
      if (response.success) {
        setProviders(response.data?.providers || []);
      }
    } catch (err) {
      showMessage('danger', 'Erro ao carregar provedores');
    } finally {
      setLoading(false);
    }
  }, [showMessage]);

  const loadActionLog = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      const response = await aiService.getActionLog({ page, per_page: 20 });
      if (response.success) {
        setActionLog(response.data?.logs || []);
        setLogPagination({
          total: response.data?.total || 0,
          page: response.data?.page || 1,
          total_pages: response.data?.total_pages || 1
        });
      }
    } catch (err) {
      showMessage('danger', 'Erro ao carregar log de ações');
    } finally {
      setLoading(false);
    }
  }, [showMessage]);

  const loadSystemPrompt = useCallback(async () => {
    setSystemPromptLoading(true);
    try {
      const response = await aiService.getSystemPrompt();
      if (response.success) {
        setSystemPrompt(response.data?.system_prompt || '');
      }
    } catch (err) {
      showMessage('danger', 'Erro ao carregar prompt de sistema');
    }
    setSystemPromptLoading(false);
  }, [showMessage]);

  const loadOcrSpaceKey = useCallback(async () => {
    setOcrSpaceKeyLoading(true);
    try {
      const response = await aiService.getOcrSpaceKey();
      if (response.success) {
        setOcrSpaceKey(response.data?.ocr_space_api_key || '');
      }
    } catch (err) {
      showMessage('danger', 'Erro ao carregar chave OCR');
    }
    setOcrSpaceKeyLoading(false);
  }, [showMessage]);

  useEffect(() => {
    loadProviders();
  }, [loadProviders]);

  const loadPromptTemplates = useCallback(async () => {
    try {
      setPromptTemplatesLoading(true);
      const response = await aiService.getPromptTemplates();
      if (response.success) {
        setPromptTemplates(response.data?.templates || []);
      }
    } catch (err) {
      showMessage('danger', 'Erro ao carregar modelos de prompt');
    } finally {
      setPromptTemplatesLoading(false);
    }
  }, [showMessage]);

  const savePromptTemplate = async (e) => {
    e.preventDefault();
    setPromptTemplateSaving(true);
    try {
      const payload = { ...promptTemplateForm };
      if (editingPromptTemplate) {
        const response = await aiService.updatePromptTemplate(editingPromptTemplate.id, payload);
        if (response.success) {
          showMessage('success', 'Modelo de prompt atualizado');
        } else {
          showMessage('danger', response.error || 'Erro ao atualizar');
        }
      } else {
        const response = await aiService.createPromptTemplate(payload);
        if (response.success) {
          showMessage('success', 'Modelo de prompt criado');
        } else {
          showMessage('danger', response.error || 'Erro ao criar');
        }
      }
      setShowPromptTemplateModal(false);
      setEditingPromptTemplate(null);
      loadPromptTemplates();
    } catch (err) {
      showMessage('danger', 'Erro ao salvar modelo de prompt');
    } finally {
      setPromptTemplateSaving(false);
    }
  };

  const deletePromptTemplate = async (template) => {
    if (!window.confirm(`Remover o modelo "${template.title}"?`)) return;
    try {
      const response = await aiService.deletePromptTemplate(template.id);
      if (response.success) {
        showMessage('success', 'Modelo de prompt removido');
        loadPromptTemplates();
      } else {
        showMessage('danger', response.error || 'Erro ao remover');
      }
    } catch (err) {
      showMessage('danger', 'Erro ao remover modelo de prompt');
    }
  };

  const openNewPromptTemplateModal = () => {
    setEditingPromptTemplate(null);
    setPromptTemplateForm({ title: '', prompt_text: '', category: '', sort_order: 0, is_active: 1 });
    setShowPromptTemplateModal(true);
  };

  const openEditPromptTemplateModal = (t) => {
    setEditingPromptTemplate(t);
    setPromptTemplateForm({
      title: t.title,
      prompt_text: t.prompt_text,
      category: t.category || '',
      sort_order: t.sort_order || 0,
      is_active: t.is_active
    });
    setShowPromptTemplateModal(true);
  };

  const loadFunctions = useCallback(async () => {
    try {
      setFunctionsLoading(true);
      const response = await aiService.getFunctions();
      if (response.success) {
        setFunctions(response.data?.functions || {});
        setFunctionsTotal(response.data?.total || 0);
      }
    } catch (err) {
      showMessage('danger', 'Erro ao carregar funções');
    } finally {
      setFunctionsLoading(false);
    }
  }, [showMessage]);

  useEffect(() => {
    if (activeTab === 'system-prompt') {
      loadSystemPrompt();
    }
    if (activeTab === 'log') {
      loadActionLog();
    }
    if (activeTab === 'settings') {
      loadOcrSpaceKey();
    }
    if (activeTab === 'prompt-templates') {
      loadPromptTemplates();
    }
    if (activeTab === 'functions') {
      loadFunctions();
    }
  }, [activeTab, loadSystemPrompt, loadActionLog, loadOcrSpaceKey, loadPromptTemplates, loadFunctions]);

  const handleProviderTypeChange = (type) => {
    const defaults = DEFAULT_BASE_URLS[type] || '';
    const defaultModel = DEFAULT_MODELS[type] || '';
    setProviderForm(prev => ({
      ...prev,
      provider_type: type,
      base_url: defaults,
      default_model: defaultModel
    }));
  };

  const handleLoadModels = async () => {
    if (!providerForm.base_url || (!providerForm.api_key && providerForm.provider_type !== 'ollama')) return;
    setProviderModelsLoading(true);
    setProviderModels([]);
    setProviderModelFilter('');
    try {
      const response = await aiService.listModelsFromProvider({
        provider_type: providerForm.provider_type,
        base_url: providerForm.base_url,
        api_key: providerForm.api_key
      });
      const data = response.data || response;
      const models = data?.models || [];
      setProviderModels(models);
      setShowModelInput(false);
    } catch (err) {
      showMessage('danger', 'Erro ao carregar modelos. Verifique a URL e API Key.');
    }
    setProviderModelsLoading(false);
  };

  const openNewProvider = () => {
    setEditingProvider(null);
    setProviderForm({
      name: '', provider_type: 'openai', base_url: DEFAULT_BASE_URLS.openai, api_key: '',
      default_model: DEFAULT_MODELS.openai, max_tokens: 4096, temperature: 0.7,
      is_active: 1, is_default: 0, sort_order: 0
    });
    setProviderModels([]);
    setShowModelInput(false);
    setProviderModelFilter('');
    setShowProviderModal(true);
  };

  const openEditProvider = (provider) => {
    setEditingProvider(provider);
    setProviderForm({
      name: provider.name || '',
      provider_type: provider.provider_type || 'openai',
      base_url: provider.base_url || '',
      api_key: provider.api_key || '',
      default_model: provider.default_model || '',
      max_tokens: provider.max_tokens || 4096,
      temperature: provider.temperature || 0.7,
      is_active: provider.is_active ?? 1,
      is_default: provider.is_default ?? 0,
      sort_order: provider.sort_order || 0
    });
    setShowProviderModal(true);
  };

  const saveProvider = async () => {
    try {
      if (editingProvider) {
        const response = await aiService.updateProvider(editingProvider.id, providerForm);
        if (response.success) {
          showMessage('success', 'Provedor atualizado com sucesso');
          setShowProviderModal(false);
          loadProviders();
        } else {
          showMessage('danger', response.error || 'Erro ao atualizar');
        }
      } else {
        const response = await aiService.createProvider(providerForm);
        if (response.success) {
          showMessage('success', 'Provedor criado com sucesso');
          setShowProviderModal(false);
          loadProviders();
        } else {
          showMessage('danger', response.error || 'Erro ao criar');
        }
      }
    } catch (err) {
      showMessage('danger', editingProvider ? 'Erro ao atualizar provedor' : 'Erro ao criar provedor');
    }
  };

  const confirmDelete = (target, type) => {
    setDeleteTarget(target);
    setDeleteType(type);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      if (deleteType === 'provider') {
        const response = await aiService.deleteProvider(deleteTarget.id);
        if (response.success) {
          showMessage('success', 'Provedor removido');
          loadProviders();
        } else {
          showMessage('danger', response.error || 'Erro ao remover');
        }
      }
      setShowDeleteModal(false);
      setDeleteTarget(null);
    } catch (err) {
      showMessage('danger', 'Erro ao remover');
    }
    setDeleting(false);
  };

  const testProviderConnection = async (id) => {
    setTestingProviders(prev => ({ ...prev, [id]: true }));
    try {
      const response = await aiService.testProvider(id);
      if (response.success) {
        showMessage('success', 'Conexão testada com sucesso!');
      } else {
        showMessage('danger', response.error || 'Falha na conexão');
      }
    } catch (err) {
      showMessage('danger', 'Erro ao testar conexão');
    }
    setTestingProviders(prev => ({ ...prev, [id]: false }));
  };

  const listModels = async (providerId) => {
    setLoadingModels(true);
    setAvailableModels([]);
    setModelsProviderId(providerId);
    try {
      const response = await aiService.listProviderModels(providerId);
      if (response.success) {
        setAvailableModels(response.data?.models || []);
      } else {
        showMessage('danger', 'Não foi possível listar modelos');
      }
    } catch (err) {
      showMessage('danger', 'Erro ao listar modelos');
    }
    setLoadingModels(false);
  };

  const handleUndo = async (logId) => {
    setUndoing(logId);
    try {
      const response = await aiService.undoAction(logId);
      if (response.success) {
        showMessage('success', 'Ação desfeita com sucesso!');
        loadActionLog();
      } else {
        showMessage('danger', response.error || 'Erro ao desfazer');
      }
    } catch (err) {
      showMessage('danger', 'Erro ao desfazer ação');
    } finally {
      setUndoing(null);
    }
  };

  const openLogDetail = async (logId) => {
    try {
      const response = await aiService.getActionLogDetail(logId);
      if (response.success) {
        setLogDetail(response.data?.log || null);
      }
    } catch (err) {
      showMessage('danger', 'Erro ao carregar detalhes');
    }
  };

  const saveSystemPrompt = async () => {
    setSystemPromptSaving(true);
    try {
      const response = await aiService.updateSystemPrompt(systemPrompt);
      if (response.success) {
        showMessage('success', 'Prompt de sistema atualizado com sucesso');
      } else {
        showMessage('danger', response.error || 'Erro ao salvar');
      }
    } catch (err) {
      showMessage('danger', 'Erro ao salvar prompt de sistema');
    }
    setSystemPromptSaving(false);
  };

  const saveOcrSpaceKey = async () => {
    setOcrSpaceKeySaving(true);
    try {
      const response = await aiService.updateOcrSpaceKey(ocrSpaceKey);
      if (response.success) {
        showMessage('success', 'Chave OCR atualizada com sucesso');
      } else {
        showMessage('danger', response.error || 'Erro ao salvar');
      }
    } catch (err) {
      showMessage('danger', 'Erro ao salvar chave OCR');
    }
    setOcrSpaceKeySaving(false);
  };

  const tabs = [
    { key: 'providers', label: 'Provedores', icon: FiServer },
    { key: 'functions', label: 'Ferramentas', icon: FiZap },
    { key: 'log', label: 'Log de Ações', icon: FiClock },
    { key: 'system-prompt', label: 'Prompt de Sistema', icon: FiFileText },
    { key: 'settings', label: 'Configurações', icon: FiSettings },
    { key: 'prompt-templates', label: 'Modelos de Prompts', icon: FiList }
  ];

  const getProviderTypeDisplay = (type) => {
    const found = PROVIDER_TYPES.find(p => p.value === type);
    return found ? found.label : type;
  };

  const paginationPages = () => {
    const total = logPagination.total_pages;
    const current = logPagination.page;
    const pages = [];
    const start = Math.max(1, current - 2);
    const end = Math.min(total, current + 2);
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  };

  const labelClass = 'd-block mb-1 fw-semibold';
  const inputClass = 'form-control-modern';
  const selectClass = 'form-control-modern';

  return (
    <div className="container-fluid py-4">
      <style>{`
        .spin-ico { animation: spin-ico 0.8s linear infinite; display: inline-block; }
        @keyframes spin-ico { to { transform: rotate(360deg); } }
        @keyframes modalSlideIn { from { opacity: 0; transform: translateY(-20px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>

      <div className="settings-container mb-4">
        <div className="card-modern shadow-sm-modern">
          <div className="card-header-modern bg-transparent border-0 pt-4 px-4 pb-0 d-flex justify-content-between align-items-center flex-wrap gap-3">
            <div>
              <h2 className="mb-1" style={{ fontSize: '1.3rem' }}>Integração de IA</h2>
              <p className="text-muted mb-0" style={{ fontSize: '0.82rem' }}>Configure provedores de IA, gerencie funções disponíveis e execute ações</p>
            </div>
          </div>
          <div className="card-body-modern px-4 pb-2 pt-3">
            <div className="d-flex gap-1" style={{
              background: '#f1f5f9', borderRadius: '12px', padding: '4px', display: 'inline-flex'
            }}>
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.key}
                    className="d-flex align-items-center gap-2"
                    onClick={() => setActiveTab(tab.key)}
                    style={{
                      padding: '8px 18px', borderRadius: '10px', border: 'none',
                      background: activeTab === tab.key ? '#ffffff' : 'transparent',
                      color: activeTab === tab.key ? '#667eea' : '#64748b',
                      fontWeight: activeTab === tab.key ? 600 : 400,
                      fontSize: '0.85rem', cursor: 'pointer',
                      boxShadow: activeTab === tab.key ? '0 2px 8px rgba(0,0,0,0.08)' : 'none',
                      transition: 'all 0.2s'
                    }}
                  >
                    <Icon size={16} />
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {message && <div className={`alert alert-${message.type} py-2 mb-4`} role="alert">{message.text}</div>}

      {/* TAB: PROVEDORES */}
      {activeTab === 'providers' && (
        <div className="settings-container">
          <div className="card-modern shadow-sm-modern">
            <div className="card-header-modern bg-transparent border-0 pt-4 px-4 pb-3 d-flex justify-content-between align-items-center flex-wrap gap-3">
              <h5 className="mb-0 fw-bold text-dark">Provedores de IA</h5>
              <div className="d-flex gap-2">
                <button type="button" className="btn-modern btn-modern-primary" onClick={openNewProvider}>
                  <FiPlus size={16} className="me-2" />Novo Provedor
                </button>
              </div>
            </div>
            <div className="card-body-modern p-4 pt-0">
              {loading && providers.length === 0 ? (
                <Loading text="Carregando provedores de IA..." subtext="Buscando credenciais e configurações de API..." />
              ) : providers.length === 0 ? (
                <div className="text-center py-5 text-muted">Nenhum provedor configurado</div>
              ) : (
                <div className="table-responsive">
                  <table className="table-modern w-100">
                    <thead>
                      <tr>
                        <th>Nome</th>
                        <th>Tipo</th>
                        <th>URL Base</th>
                        <th>Modelo</th>
                        <th>Tokens</th>
                        <th>Temp.</th>
                        <th>Key</th>
                        <th>Ativo</th>
                        <th>Padrão</th>
                        <th className="text-end">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {providers.map((prov) => (
                        <tr key={prov.id}>
                          <td className="fw-semibold text-dark">{prov.name}</td>
                          <td><span className="badge-modern badge-modern-secondary">{getProviderTypeDisplay(prov.provider_type)}</span></td>
                          <td style={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            <code style={{ fontSize: '0.75rem' }}>{prov.base_url || '-'}</code>
                          </td>
                          <td className="font-monospace" style={{ fontSize: '0.8rem' }}>{prov.default_model || '-'}</td>
                          <td className="text-muted" style={{ fontSize: '0.8rem' }}>{prov.max_tokens}</td>
                          <td className="text-muted" style={{ fontSize: '0.8rem' }}>{prov.temperature}</td>
                          <td>{prov.has_api_key ? <FiCheck className="text-success" size={16} /> : <FiX className="text-danger" size={16} />}</td>
                          <td>{prov.is_active ? <span className="text-success">Sim</span> : <span className="text-muted">Não</span>}</td>
                          <td>{prov.is_default ? <span className="text-primary fw-semibold">Padrão</span> : '-'}</td>
                          <td>
                            <div className="d-flex justify-content-end align-items-center gap-2">
                              <button type="button" className="btn-modern btn-modern-light btn-modern-sm" title="Editar" onClick={() => openEditProvider(prov)}
                                style={{ padding: '6px 10px' }}>
                                <FiEdit2 size={14} />
                              </button>
                              <button type="button" className="btn-modern btn-modern-light btn-modern-sm" title="Testar conexão"
                                disabled={testingProviders[prov.id]} onClick={() => testProviderConnection(prov.id)}
                                style={{ padding: '6px 10px' }}>
                                {testingProviders[prov.id] ? <div className="spinner-border spinner-border-sm" role="status" /> : <FiRefreshCw size={14} />}
                              </button>
                              <button type="button" className="btn-modern btn-modern-light btn-modern-sm" title="Listar modelos"
                                disabled={loadingModels} onClick={() => listModels(prov.id)}
                                style={{ padding: '6px 10px' }}>
                                {loadingModels ? <div className="spinner-border spinner-border-sm" role="status" /> : <FiList size={14} />}
                              </button>
                              <button type="button" className="btn-modern btn-modern-light-danger btn-modern-sm" title="Excluir"
                                onClick={() => confirmDelete(prov, 'provider')}
                                style={{ padding: '6px 10px' }}>
                                <FiTrash2 size={14} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {availableModels.length > 0 && (
                <div className="mt-4" style={{
                  background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '16px 20px'
                }}>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="fw-bold" style={{ fontSize: '0.85rem' }}>Modelos Disponíveis</span>
                    <button type="button" className="btn-modern btn-modern-light btn-modern-sm" onClick={() => { setAvailableModels([]); setModelsProviderId(null); }}
                      style={{ padding: '4px 8px' }}>
                      <FiX size={14} />
                    </button>
                  </div>
                  <div className="d-flex flex-wrap gap-2">
                    {availableModels.map((m, i) => (
                      <span key={i}
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '0.72rem',
                          padding: '3px 8px', backgroundColor: '#3b82f6', color: '#ffffff',
                          borderRadius: '6px', fontWeight: '500', cursor: 'pointer'
                        }}
                        onClick={() => {
                          const provId = modelsProviderId || providers.find(p => p.is_default)?.id;
                          if (provId) {
                            aiService.updateProvider(provId, { default_model: m.id }).then(() => loadProviders());
                          }
                        }}
                        title="Clique para definir como modelo padrão"
                      >
                        {m.id || m.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {loadingModels && (
                <div className="text-center py-3 text-muted">
                  <div className="spinner-border spinner-border-sm me-2" role="status" />Listando modelos...
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* TAB: FERRAMENTAS */}
      {activeTab === 'functions' && (
        <div className="settings-container">
          <div className="card-modern shadow-sm-modern">
            <div className="card-header-modern bg-transparent border-0 pt-4 px-4 pb-3 d-flex justify-content-between align-items-center flex-wrap gap-3">
              <h5 className="mb-0 fw-bold text-dark">Ferramentas Disponíveis</h5>
              <button type="button" className="btn-modern btn-modern-light" onClick={loadFunctions} disabled={functionsLoading}>
                <FiRefreshCw size={16} className={functionsLoading ? 'spin-ico me-2' : 'me-2'} />
                Atualizar
              </button>
            </div>
            <div className="card-body-modern p-4 pt-0">
              <p className="text-muted mb-3" style={{ fontSize: '0.82rem' }}>
                Estas são as funções que a IA pode usar para interagir com o sistema. Total: <strong>{functionsTotal}</strong> ferramentas.
              </p>
              {functionsLoading ? (
                <Loading text="Carregando catálogo de ferramentas..." subtext="Mapeando rotas e parâmetros para chamadas de ferramentas da IA..." />
              ) : Object.keys(functions).length === 0 ? (
                <div className="text-center py-5 text-muted">Nenhuma ferramenta disponível</div>
              ) : (
                Object.entries(functions).map(([section, fns]) => (
                  <div key={section} className="mb-4">
                    <h6 className="fw-bold text-dark mb-2" style={{ fontSize: '0.9rem', textTransform: 'capitalize', borderBottom: '2px solid #e2e8f0', paddingBottom: '8px' }}>
                      {section} <span className="badge-modern badge-modern-secondary ms-2">{fns.length}</span>
                    </h6>
                    <div className="table-responsive">
                      <table className="table-modern w-100" style={{ fontSize: '0.8rem' }}>
                        <thead>
                          <tr>
                            <th>Função</th>
                            <th>Descrição</th>
                            <th>Método</th>
                            <th>Endpoint</th>
                            <th>Permissão</th>
                          </tr>
                        </thead>
                        <tbody>
                          {fns.map((fn) => (
                            <tr key={fn.function_key}>
                              <td>
                                <code className="font-monospace" style={{ fontSize: '0.75rem', fontWeight: 600 }}>
                                  {fn.function_key}
                                </code>
                                <div style={{ fontSize: '0.72rem', color: '#64748b', marginTop: 2 }}>
                                  {fn.name}
                                </div>
                              </td>
                              <td style={{ maxWidth: 300, fontSize: '0.75rem' }}>
                                {fn.description}
                              </td>
                              <td>
                                <span className="badge-modern" style={{
                                  background: fn.http_method === 'GET' ? '#dbeafe' : fn.http_method === 'POST' ? '#dcfce7' : fn.http_method === 'PUT' ? '#fef3c7' : '#fee2e2',
                                  color: fn.http_method === 'GET' ? '#1e40af' : fn.http_method === 'POST' ? '#166534' : fn.http_method === 'PUT' ? '#92400e' : '#991b1b'
                                }}>
                                  {fn.http_method}
                                </span>
                              </td>
                              <td>
                                <code style={{ fontSize: '0.72rem', background: '#f1f5f9', padding: '2px 6px', borderRadius: '4px' }}>
                                  {fn.endpoint_path}
                                </code>
                              </td>
                              <td>
                                <span className="badge-modern" style={{
                                  background: fn.required_permission === 'manage' ? '#fef3c7' : '#dbeafe',
                                  color: fn.required_permission === 'manage' ? '#92400e' : '#1e40af'
                                }}>
                                  {fn.required_permission}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* TAB: LOG */}
      {activeTab === 'log' && (
        <div className="settings-container">
          <div className="card-modern shadow-sm-modern">
            <div className="card-header-modern bg-transparent border-0 pt-4 px-4 pb-3 d-flex justify-content-between align-items-center flex-wrap gap-3">
              <h5 className="mb-0 fw-bold text-dark">Log de Ações da IA</h5>
              <button type="button" className="btn-modern btn-modern-light" onClick={() => loadActionLog()} disabled={loading}>
                <FiRefreshCw size={16} className={loading ? 'spin-ico me-2' : 'me-2'} />
                Atualizar
              </button>
            </div>
            <div className="card-body-modern p-4 pt-0">
              {loading && actionLog.length === 0 ? (
                <Loading text="Carregando histórico de ações..." subtext="Recuperando logs de execução e auditoria..." />
              ) : actionLog.length === 0 ? (
                <div className="text-center py-5 text-muted">Nenhuma ação registrada</div>
              ) : (
                <>
                  <div className="table-responsive">
                    <table className="table-modern w-100">
                      <thead>
                        <tr>
                          <th>Data</th>
                          <th>Usuário</th>
                          <th>Função</th>
                          <th>Tipo</th>
                          <th>Resumo</th>
                          <th>Provedor</th>
                          <th>Sucesso</th>
                          <th>Desfeito</th>
                          <th className="text-end">Ações</th>
                        </tr>
                      </thead>
                      <tbody>
                        {actionLog.map((log) => (
                          <tr key={log.id}>
                            <td style={{ whiteSpace: 'nowrap', fontSize: '0.8rem' }}>
                              {new Date(log.created_at).toLocaleString('pt-BR')}
                            </td>
                            <td className="text-muted" style={{ fontSize: '0.85rem' }}>{log.user_name || log.user_id}</td>
                            <td><code className="font-monospace" style={{ fontSize: '0.75rem' }}>{log.function_key}</code></td>
                            <td>
                              <span className="badge-modern" style={{
                                background: log.action_type === 'create' ? '#dcfce7' : log.action_type === 'update' ? '#fef3c7' : log.action_type === 'delete' ? '#fee2e2' : log.action_type === 'read' ? '#dbeafe' : '#f1f5f9',
                                color: log.action_type === 'create' ? '#166534' : log.action_type === 'update' ? '#92400e' : log.action_type === 'delete' ? '#991b1b' : log.action_type === 'read' ? '#1e40af' : '#475569'
                              }}>
                                {log.action_type}
                              </span>
                            </td>
                            <td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              <span className="text-muted" style={{ fontSize: '0.8rem' }}>{log.request_summary || '-'}</span>
                            </td>
                            <td className="text-muted" style={{ fontSize: '0.8rem' }}>{log.provider_name || '-'}</td>
                            <td>{(log.success === 1 || log.success === '1') ? <FiCheck className="text-success" size={16} /> : <FiX className="text-danger" size={16} />}</td>
                            <td>
                              {(log.undone === 1 || log.undone === '1') ? (
                                <span className="badge-modern" style={{ background: '#e2e8f0', color: '#475569' }}>Sim</span>
                              ) : (
                                <span className="text-muted" style={{ fontSize: '0.8rem' }}>Não</span>
                              )}
                            </td>
                            <td>
                              <div className="d-flex justify-content-end align-items-center gap-2">
                                <button type="button" className="btn-modern btn-modern-light btn-modern-sm" title="Detalhes"
                                  onClick={() => openLogDetail(log.id)} style={{ padding: '6px 10px' }}>
                                  <FiCode size={14} />
                                </button>
                  {(log.undone === 0 || log.undone === '0' || !log.undone) && log.undo_data && (
                  <button type="button" className="btn-modern btn-modern-light btn-modern-sm" title="Desfazer"
                    onClick={() => handleUndo(log.id)} disabled={undoing === log.id}
                    style={{ padding: '6px 10px', opacity: undoing === log.id ? 0.6 : 1 }}>
                    {undoing === log.id ? <FiLoader className="spin-ico" size={14} /> : <FiRefreshCw size={14} />}
                  </button>
                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {logPagination.total_pages > 1 && (
                    <div className="d-flex justify-content-between align-items-center mt-3 px-2">
                      <small className="text-muted">
                        Mostrando {((logPagination.page - 1) * 20) + 1}-{Math.min(logPagination.page * 20, logPagination.total)} de {logPagination.total} registros
                      </small>
                      <div className="d-flex gap-1 align-items-center">
                        <button className="btn-modern btn-modern-light btn-modern-sm" disabled={logPagination.page <= 1}
                          onClick={() => loadActionLog(logPagination.page - 1)}>Anterior</button>
                        {paginationPages().map(p => (
                          <button key={p} className={`btn-modern btn-modern-sm ${p === logPagination.page ? 'btn-modern-primary' : 'btn-modern-light'}`}
                            onClick={() => loadActionLog(p)}>{p}</button>
                        ))}
                        <button className="btn-modern btn-modern-light btn-modern-sm" disabled={logPagination.page >= logPagination.total_pages}
                          onClick={() => loadActionLog(logPagination.page + 1)}>Próximo</button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* TAB: PROMPT DE SISTEMA */}
      {activeTab === 'system-prompt' && (
        <div className="settings-container">
          <div className="card-modern shadow-sm-modern">
            <div className="card-header-modern bg-transparent border-0 pt-4 px-4 pb-3 d-flex justify-content-between align-items-center flex-wrap gap-3">
              <h5 className="mb-0 fw-bold text-dark">Prompt de Sistema (Global)</h5>
              <button type="button" className="btn-modern btn-modern-primary" onClick={saveSystemPrompt} disabled={systemPromptSaving}>
                <FiSave size={16} className="me-2" />
                {systemPromptSaving ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
            <div className="card-body-modern p-4 pt-0">
              <p className="text-muted mb-3" style={{ fontSize: '0.82rem' }}>
                Este prompt e enviado para a IA em toda conversa, antes das funcoes disponiveis.
                As funcoes ativas sao automaticamente anexadas ao final deste texto.
                Se vazio, o sistema gera um prompt padrao automaticamente.
              </p>
              <div className="form-group-modern">
                {systemPromptLoading ? (
                  <Loading text="Carregando diretriz do sistema..." subtext="Buscando template de prompt ativo..." />
                ) : (
                  <textarea
                    className="form-control-modern"
                    rows={20}
                    value={systemPrompt}
                    onChange={e => setSystemPrompt(e.target.value)}
                    placeholder="Voce e um assistente de IA especializado em gerenciar a plataforma..."
                    style={{
                      resize: 'vertical',
                      fontFamily: 'monospace',
                      fontSize: '0.8rem',
                      lineHeight: 1.6,
                      width: '100%'
                    }}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* TAB: CONFIGURAÇÕES */}
      {activeTab === 'settings' && (
        <div className="settings-container">
          <div className="card-modern shadow-sm-modern">
            <div className="card-header-modern bg-transparent border-0 pt-4 px-4 pb-3 d-flex justify-content-between align-items-center flex-wrap gap-3">
              <h5 className="mb-0 fw-bold text-dark">Configurações de IA</h5>
              <button type="button" className="btn-modern btn-modern-primary" onClick={saveOcrSpaceKey} disabled={ocrSpaceKeySaving}>
                <FiSave size={16} className="me-2" />
                {ocrSpaceKeySaving ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
            <div className="card-body-modern p-4 pt-0">
              <p className="text-muted mb-3" style={{ fontSize: '0.82rem' }}>
                Configure a chave da API OCR.space para processamento de imagens e PDFs enviados no chat da IA.
                Deixe vazio se não precisar de OCR.
              </p>
              <div className="form-group-modern mb-3">
                <label className={labelClass}>
                  <FiKey size={14} className="me-1" />
                  OCR.space API Key
                </label>
                {ocrSpaceKeyLoading ? (
                  <div className="text-center py-3"><div className="spinner-border spinner-border-sm" role="status" /></div>
                ) : (
                  <input
                    className={inputClass}
                    type="password"
                    value={ocrSpaceKey}
                    onChange={e => setOcrSpaceKey(e.target.value)}
                    placeholder="Insira sua chave da API OCR.space"
                    style={{ maxWidth: '500px' }}
                  />
                )}
                <small className="text-muted d-block mt-1" style={{ fontSize: '0.75rem' }}>
                  Obtenha sua chave gratuita em <a href="https://ocr.space/ocrapi" target="_blank" rel="noopener noreferrer">ocr.space/ocrapi</a>
                </small>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* TAB: MODELOS DE PROMPTS */}
      {activeTab === 'prompt-templates' && (
        <div className="settings-container">
          <div className="card-modern shadow-sm-modern">
            <div className="card-header-modern bg-transparent border-0 pt-4 px-4 pb-3 d-flex justify-content-between align-items-center flex-wrap gap-3">
              <h5 className="mb-0 fw-bold text-dark">Modelos de Prompts</h5>
              <button type="button" className="btn-modern btn-modern-primary" onClick={openNewPromptTemplateModal}>
                <FiPlus size={16} className="me-2" />Novo Modelo
              </button>
            </div>
            <div className="card-body-modern p-4 pt-0">
              <p className="text-muted mb-3" style={{ fontSize: '0.82rem' }}>
                Gerencie os modelos de prompt pre-prontos que aparecem como sugestao no chat da IA.
                Os prompts ativos sao exibidos como botoes de sugestao na tela inicial do chat.
              </p>
              {promptTemplatesLoading ? (
                <Loading text="Carregando modelos de prompts..." subtext="Carregando atalhos e sugestões rápidas..." />
              ) : promptTemplates.length === 0 ? (
                <div className="text-center py-5 text-muted">
                  <p>Nenhum modelo de prompt cadastrado.</p>
                  <button type="button" className="btn-modern btn-modern-primary" onClick={openNewPromptTemplateModal}>
                    <FiPlus size={16} className="me-2" />Criar primeiro modelo
                  </button>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table-modern w-100">
                    <thead>
                      <tr>
                        <th style={{width:'40px'}}>Ordem</th>
                        <th>Titulo</th>
                        <th>Prompt</th>
                        <th>Categoria</th>
                        <th style={{width:'80px'}}>Ativo</th>
                        <th style={{width:'120px', textAlign:'right'}}>Acoes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {promptTemplates.map((t) => (
                        <tr key={t.id}>
                          <td><span className="text-muted small">{t.sort_order}</span></td>
                          <td><span className="fw-medium">{t.title}</span></td>
                          <td>
                            <code style={{
                              fontSize: '0.72rem', background: '#f1f5f9', padding: '2px 6px',
                              borderRadius: '4px', display: 'block', maxWidth: '400px',
                              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                            }}>
                              {t.prompt_text}
                            </code>
                          </td>
                          <td><span className="text-muted small">{t.category || '-'}</span></td>
                          <td>
                            <span className={`badge-modern ${Number(t.is_active) === 1 ? 'badge-modern-success' : 'badge-modern-light'}`}>
                              {Number(t.is_active) === 1 ? 'Sim' : 'Nao'}
                            </span>
                          </td>
                          <td style={{textAlign:'right'}}>
                            <div className="d-flex justify-content-end gap-1">
                              <button type="button" className="btn-modern btn-modern-light btn-modern-sm"
                                onClick={() => openEditPromptTemplateModal(t)} title="Editar">
                                <FiEdit2 size={14} />
                              </button>
                              <button type="button" className="btn-modern btn-modern-light-danger btn-modern-sm"
                                onClick={() => deletePromptTemplate(t)} title="Excluir">
                                <FiTrash2 size={14} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* MODAL: PROMPT TEMPLATE */}
      {showPromptTemplateModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 9999, backdropFilter: 'blur(4px)', padding: '20px'
        }}
        >
          <div style={{
            background: 'white', borderRadius: '20px', width: '100%', maxWidth: '650px',
            maxHeight: '90vh', boxShadow: '0 24px 60px rgba(0,0,0,0.25)',
            animation: 'modalSlideIn 0.25s ease',
            display: 'flex', flexDirection: 'column', overflow: 'hidden'
          }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              padding: '24px 28px 20px', borderBottom: '1px solid #f1f5f9',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0
            }}>
              <div className="d-flex align-items-center gap-3">
                <div style={{
                  width: '44px', height: '44px', borderRadius: '12px',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white',
                  display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}>
                  <FiList size={20} />
                </div>
                <div>
                  <h5 style={{ margin: 0, fontWeight: 700, fontSize: '1rem' }}>{editingPromptTemplate ? 'Editar Modelo de Prompt' : 'Novo Modelo de Prompt'}</h5>
                  <p style={{ margin: 0, fontSize: '0.78rem', color: '#64748b' }}>Modelo de prompt pre-pronto para o chat IA</p>
                </div>
              </div>
              <button type="button" onClick={() => setShowPromptTemplateModal(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', borderRadius: '8px' }}>
                <FiX size={20} color="#64748b" />
              </button>
            </div>

            <form onSubmit={savePromptTemplate} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
              <div style={{ padding: '24px 28px', overflowY: 'auto', flex: 1 }}>
                <div className="form-group-modern mb-3">
                  <label className={labelClass}>Titulo <span className="text-danger">*</span></label>
                  <input className={inputClass} value={promptTemplateForm.title}
                    onChange={e => setPromptTemplateForm(p => ({ ...p, title: e.target.value }))}
                    placeholder="Ex: Status dos relatorios do ultimo mes" />
                </div>
                <div className="form-group-modern mb-3">
                  <label className={labelClass}>Texto do Prompt <span className="text-danger">*</span></label>
                  <textarea className={inputClass} rows={4} value={promptTemplateForm.prompt_text}
                    onChange={e => setPromptTemplateForm(p => ({ ...p, prompt_text: e.target.value }))}
                    placeholder="Ex: Status dos relatorios do ultimo mes"
                    style={{ resize: 'vertical' }} />
                </div>
                <div className="row g-3 mb-3">
                  <div className="col-md-6">
                    <div className="form-group-modern mb-0">
                      <label className={labelClass}>Categoria</label>
                      <input className={inputClass} value={promptTemplateForm.category}
                        onChange={e => setPromptTemplateForm(p => ({ ...p, category: e.target.value }))}
                        placeholder="Ex: Relatorios, Metas" />
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="form-group-modern mb-0">
                      <label className={labelClass}>Ordem</label>
                      <input className={inputClass} type="number" min="0" value={promptTemplateForm.sort_order}
                        onChange={e => setPromptTemplateForm(p => ({ ...p, sort_order: parseInt(e.target.value) || 0 }))} />
                    </div>
                  </div>
                  <div className="col-md-3 d-flex align-items-end">
                    <div className="form-check-modern d-flex align-items-center gap-2 pb-2">
                      <input className="form-check-input" type="checkbox" id="ptActive"
                        checked={promptTemplateForm.is_active === 1}
                        onChange={e => setPromptTemplateForm(p => ({ ...p, is_active: e.target.checked ? 1 : 0 }))}
                        style={{ width: '18px', height: '18px', cursor: 'pointer' }} />
                      <label className="form-check-label" htmlFor="ptActive" style={{ cursor: 'pointer', fontSize: '0.85rem' }}>Ativo</label>
                    </div>
                  </div>
                </div>
              </div>

              <div style={{ padding: '20px 28px 24px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                <button type="button" className="btn-modern btn-modern-light" onClick={() => setShowPromptTemplateModal(false)}>Cancelar</button>
                <button type="submit" className="btn-modern btn-modern-primary" disabled={!promptTemplateForm.title || !promptTemplateForm.prompt_text || promptTemplateSaving}>
                  <FiSave size={16} className="me-1" />{promptTemplateSaving ? 'Salvando...' : (editingPromptTemplate ? 'Atualizar' : 'Criar')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL: LOG DETAIL */}
      {logDetail && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 9999, backdropFilter: 'blur(4px)', padding: '20px'
        }}
        >
          <div style={{
            background: 'white', borderRadius: '20px', width: '100%', maxWidth: '720px',
            maxHeight: '90vh', boxShadow: '0 24px 60px rgba(0,0,0,0.25)',
            animation: 'modalSlideIn 0.25s ease', display: 'flex', flexDirection: 'column', overflow: 'hidden'
          }}
          >
            <div style={{
              padding: '24px 28px 20px', borderBottom: '1px solid #f1f5f9',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              flexShrink: 0
            }}>
              <div className="d-flex align-items-center gap-3">
                <div style={{
                  width: '44px', height: '44px', borderRadius: '12px',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white',
                  display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}>
                  <FiCode size={20} />
                </div>
                <div>
                  <h5 style={{ margin: 0, fontWeight: 700, fontSize: '1rem' }}>Detalhes da Ação</h5>
                  <p style={{ margin: 0, fontSize: '0.78rem', color: '#64748b' }}>Log de execução do agente IA</p>
                </div>
              </div>
              <button type="button" onClick={() => setLogDetail(null)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', borderRadius: '8px' }}>
                <FiX size={20} color="#64748b" />
              </button>
            </div>
            <div style={{ padding: '24px 28px', overflowY: 'auto', flex: 1 }}>
              <div className="row mb-3">
                <div className="col-6">
                  <label className="d-block mb-1 fw-semibold" style={{ fontSize: '0.8rem' }}>Função</label>
                  <p><code className="font-monospace">{logDetail.function_key}</code></p>
                </div>
                <div className="col-6">
                  <label className="d-block mb-1 fw-semibold" style={{ fontSize: '0.8rem' }}>Tipo</label>
                  <p><span className="badge-modern" style={{
                    background: logDetail.action_type === 'create' ? '#dcfce7' : logDetail.action_type === 'update' ? '#fef3c7' : logDetail.action_type === 'delete' ? '#fee2e2' : '#f1f5f9',
                    color: logDetail.action_type === 'create' ? '#166534' : logDetail.action_type === 'update' ? '#92400e' : logDetail.action_type === 'delete' ? '#991b1b' : '#475569'
                  }}>{logDetail.action_type}</span></p>
                </div>
              </div>
              <div className="mb-3">
                <label className="d-block mb-1 fw-semibold" style={{ fontSize: '0.8rem' }}>Resumo</label>
                <p className="text-muted" style={{ fontSize: '0.85rem' }}>{logDetail.request_summary || '-'}</p>
              </div>
              <div className="row mb-3">
                <div className="col-6">
                  <label className="d-block mb-1 fw-semibold" style={{ fontSize: '0.8rem' }}>Status</label>
                  <p>{logDetail.success ? <span className="text-success fw-semibold">Sucesso</span> : <span className="text-danger fw-semibold">Falha</span>}</p>
                </div>
                <div className="col-6">
                  <label className="d-block mb-1 fw-semibold" style={{ fontSize: '0.8rem' }}>Desfeito</label>
                  <p>{logDetail.undone ? `Sim (${logDetail.undone_at ? new Date(logDetail.undone_at).toLocaleString('pt-BR') : ''})` : 'Não'}</p>
                </div>
              </div>
              <div className="mb-3">
                <label className="d-block mb-1 fw-semibold" style={{ fontSize: '0.8rem' }}>Dados Enviados</label>
                <div style={{ background: '#f8fafc', padding: '12px', borderRadius: '8px', maxHeight: 180, overflow: 'auto', fontSize: '0.75rem' }}>
                  <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                    {(() => { try { return JSON.stringify(JSON.parse(logDetail.request_data || '{}'), null, 2); } catch { return logDetail.request_data || '-'; } })()}
                  </pre>
                </div>
              </div>
              <div className="mb-3">
                <label className="d-block mb-1 fw-semibold" style={{ fontSize: '0.8rem' }}>Resposta</label>
                <div style={{ background: '#1e293b', color: '#e2e8f0', padding: '12px', borderRadius: '8px', maxHeight: 180, overflow: 'auto', fontSize: '0.75rem' }}>
                  <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                    {(() => { try { return JSON.stringify(JSON.parse(logDetail.response_data || '{}'), null, 2); } catch { return logDetail.response_data || '-'; } })()}
                  </pre>
                </div>
              </div>
              {logDetail.undo_data && (
                <div className="mb-3">
                  <label className="d-block mb-1 fw-semibold" style={{ fontSize: '0.8rem' }}>Dados para Undo</label>
                  <div style={{ background: '#fffbeb', border: '1px solid #fbbf24', padding: '12px', borderRadius: '8px', maxHeight: 150, overflow: 'auto', fontSize: '0.75rem' }}>
                    <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                      {(() => { try { return JSON.stringify(JSON.parse(logDetail.undo_data), null, 2); } catch { return logDetail.undo_data; } })()}
                    </pre>
                  </div>
                </div>
              )}
            </div>
            <div style={{ padding: '20px 28px 24px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'flex-end' }}>
              <button type="button" className="btn-modern btn-modern-light" onClick={() => setLogDetail(null)}>Fechar</button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL: DELETE CONFIRMATION */}
      {showDeleteModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 9999, backdropFilter: 'blur(4px)', padding: '20px'
        }}
          onClick={(e) => e.target === e.currentTarget && setShowDeleteModal(false)}
        >
          <div style={{
            background: 'white', borderRadius: '20px', width: '100%', maxWidth: '450px',
            boxShadow: '0 24px 60px rgba(0,0,0,0.25)',
            animation: 'modalSlideIn 0.25s ease', overflow: 'hidden'
          }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ padding: '32px 28px 20px', textAlign: 'center' }}>
              <div style={{
                width: '64px', height: '64px', borderRadius: '50%', background: '#fee2e2', color: '#dc2626',
                display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px'
              }}>
                <FiTrash2 size={32} />
              </div>
              <h4 style={{ margin: '0 0 8px', fontWeight: 700, fontSize: '1.25rem', color: '#1e293b' }}>
                Confirmar Exclusão
              </h4>
              <p style={{ margin: 0, fontSize: '0.9rem', color: '#64748b' }}>
                {deleteType === 'provider' ? `Remover o provedor "${deleteTarget?.name}"?` : `Remover a função "${deleteTarget?.name}"?`}
              </p>
            </div>
            <div style={{ padding: '0 28px 20px', textAlign: 'center' }}>
              <div style={{ padding: '12px 16px', background: '#fef3c7', border: '1px solid #fbbf24', borderRadius: '8px', fontSize: '0.85rem', color: '#92400e' }}>
                Esta ação não pode ser desfeita. Os dados serão permanentemente removidos.
              </div>
            </div>
            <div style={{ padding: '16px 28px 24px', display: 'flex', justifyContent: 'center', gap: '12px' }}>
              <button type="button" className="btn-modern btn-modern-secondary" onClick={() => setShowDeleteModal(false)} disabled={deleting}>
                Cancelar
              </button>
              <button type="button" className="btn-modern btn-modern-danger" onClick={handleDelete} disabled={deleting}>
                <FiTrash2 className="me-2" size={16} />
                {deleting ? 'Excluindo...' : 'Sim, Excluir'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL: PROVEDOR */}
      {showProviderModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 9999, backdropFilter: 'blur(4px)', padding: '20px'
        }}>
          <div style={{
            background: 'white', borderRadius: '20px', width: '100%', maxWidth: '650px',
            maxHeight: '90vh', boxShadow: '0 24px 60px rgba(0,0,0,0.25)',
            animation: 'modalSlideIn 0.25s ease',
            display: 'flex', flexDirection: 'column', overflow: 'hidden'
          }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              padding: '24px 28px 20px', borderBottom: '1px solid #f1f5f9',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0
            }}>
              <div className="d-flex align-items-center gap-3">
                <div style={{
                  width: '44px', height: '44px', borderRadius: '12px',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white',
                  display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}>
                  <FiServer size={20} />
                </div>
                <div>
                  <h5 style={{ margin: 0, fontWeight: 700, fontSize: '1rem' }}>{editingProvider ? 'Editar Provedor' : 'Novo Provedor'}</h5>
                  <p style={{ margin: 0, fontSize: '0.78rem', color: '#64748b' }}>Configure um provedor de IA</p>
                </div>
              </div>
              <button type="button" onClick={() => setShowProviderModal(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', borderRadius: '8px' }}>
                <FiX size={20} color="#64748b" />
              </button>
            </div>

            <form onSubmit={(e) => { e.preventDefault(); saveProvider(); }} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
              <div style={{ padding: '24px 28px', overflowY: 'auto', flex: 1 }}>
                <div className="form-group-modern mb-3">
                  <label className={labelClass}>Nome <span className="text-danger">*</span></label>
                  <input className={inputClass} value={providerForm.name}
                    onChange={e => setProviderForm(p => ({ ...p, name: e.target.value }))} placeholder="Meu provedor" />
                </div>
                <div className="form-group-modern mb-3">
                  <label className={labelClass}>Tipo <span className="text-danger">*</span></label>
                  <select className={selectClass} value={providerForm.provider_type}
                    onChange={e => handleProviderTypeChange(e.target.value)}>
                    {PROVIDER_TYPES.map(t => (<option key={t.value} value={t.value}>{t.label}</option>))}
                  </select>
                </div>
                <div className="form-group-modern mb-3">
                  <label className={labelClass}>URL Base</label>
                  <input className={inputClass} value={providerForm.base_url}
                    onChange={e => setProviderForm(p => ({ ...p, base_url: e.target.value }))}
                    placeholder="https://api.openai.com/v1/chat/completions" />
                </div>
                <div className="form-group-modern mb-3">
                  <label className={labelClass}>API Key</label>
                  <input className={inputClass} type="password" value={providerForm.api_key}
                    onChange={e => setProviderForm(p => ({ ...p, api_key: e.target.value }))}
                    placeholder={editingProvider ? 'Deixe vazio para manter a atual' : 'sk-... ou AIzaSy... (Gemini)'} />
                </div>
                <div className="form-group-modern mb-3">
                  <label className={labelClass}>Modelo Padrão</label>
                  <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                    {providerModels.length > 0 && !showModelInput ? (
                      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <input className={inputClass} value={providerModelFilter}
                          onChange={e => setProviderModelFilter(e.target.value)}
                          placeholder="Filtrar modelos..." style={{ fontSize: '0.78rem' }} />
                        <select className={selectClass} value={providerForm.default_model}
                          onChange={e => {
                            if (e.target.value === '__manual__') {
                              setShowModelInput(true);
                              setProviderModelFilter('');
                            } else {
                              setProviderForm(p => ({ ...p, default_model: e.target.value }));
                            }
                          }}>
                          <option value="">Selecione um modelo...</option>
                          {providerModels.filter(m =>
                            !providerModelFilter ||
                            (m.id && m.id.toLowerCase().includes(providerModelFilter.toLowerCase())) ||
                            (m.name && m.name.toLowerCase().includes(providerModelFilter.toLowerCase()))
                          ).map(m => (
                            <option key={m.id} value={m.id}>{m.name || m.id}</option>
                          ))}
                          <option value="__manual__">Digitar manualmente...</option>
                        </select>
                      </div>
                    ) : (
                      <input className={inputClass} value={providerForm.default_model}
                        onChange={e => setProviderForm(p => ({ ...p, default_model: e.target.value }))} placeholder="gpt-4" style={{ flex: 1 }} />
                    )}
                    <button type="button" onClick={handleLoadModels} disabled={providerModelsLoading || !providerForm.base_url || (!providerForm.api_key && providerForm.provider_type !== 'ollama')}
                      style={{
                        padding: '7px 12px', borderRadius: '8px', border: '1px solid var(--border)',
                        background: providerModelsLoading || !providerForm.base_url || (!providerForm.api_key && providerForm.provider_type !== 'ollama') ? '#f1f5f9' : 'var(--primary)',
                        color: providerModelsLoading || !providerForm.base_url || (!providerForm.api_key && providerForm.provider_type !== 'ollama') ? '#94a3b8' : 'white',
                        cursor: providerModelsLoading || !providerForm.base_url || (!providerForm.api_key && providerForm.provider_type !== 'ollama') ? 'not-allowed' : 'pointer',
                        fontSize: '0.78rem', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0
                      }}>
                      {providerModelsLoading ? <FiLoader className="spin-ico" size={14} /> : <FiRefreshCw size={14} />}
                      {providerModelsLoading ? 'Carregando...' : 'Carregar Modelos'}
                    </button>
                  </div>
                  {providerModels.length > 0 && (
                    <small style={{ color: '#64748b', fontSize: '0.72rem', marginTop: '4px', display: 'block' }}>
                      {providerModels.filter(m =>
                        !providerModelFilter ||
                        (m.id && m.id.toLowerCase().includes(providerModelFilter.toLowerCase())) ||
                        (m.name && m.name.toLowerCase().includes(providerModelFilter.toLowerCase()))
                      ).length} de {providerModels.length} modelos. Selecione ou digite manualmente.
                    </small>
                  )}
                </div>
                <div className="row mb-3">
                  <div className="col-6">
                    <div className="form-group-modern">
                      <label className={labelClass}>Max Tokens</label>
                      <input className={inputClass} type="number" value={providerForm.max_tokens}
                        onChange={e => setProviderForm(p => ({ ...p, max_tokens: parseInt(e.target.value) || 4096 }))} />
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="form-group-modern">
                      <label className={labelClass}>Temperatura</label>
                      <input className={inputClass} type="number" step="0.1" min="0" max="2" value={providerForm.temperature}
                        onChange={e => setProviderForm(p => ({ ...p, temperature: parseFloat(e.target.value) || 0.7 }))} />
                    </div>
                  </div>
                </div>
                <div className="row mb-3">
                  <div className="col-6">
                    <div className="form-check-modern d-flex align-items-center gap-2">
                      <input className="form-check-input" type="checkbox" id="providerActive"
                        checked={providerForm.is_active === 1}
                        onChange={e => setProviderForm(p => ({ ...p, is_active: e.target.checked ? 1 : 0 }))}
                        style={{ width: '18px', height: '18px', cursor: 'pointer' }} />
                      <label className="form-check-label" htmlFor="providerActive" style={{ cursor: 'pointer', fontSize: '0.85rem' }}>Ativo</label>
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="form-check-modern d-flex align-items-center gap-2">
                      <input className="form-check-input" type="checkbox" id="providerDefault"
                        checked={providerForm.is_default === 1}
                        onChange={e => setProviderForm(p => ({ ...p, is_default: e.target.checked ? 1 : 0 }))}
                        style={{ width: '18px', height: '18px', cursor: 'pointer' }} />
                      <label className="form-check-label" htmlFor="providerDefault" style={{ cursor: 'pointer', fontSize: '0.85rem' }}>Padrão</label>
                    </div>
                  </div>
                </div>
                <div className="form-group-modern">
                  <label className={labelClass}>Ordem</label>
                  <input className={inputClass} type="number" value={providerForm.sort_order}
                    onChange={e => setProviderForm(p => ({ ...p, sort_order: parseInt(e.target.value) || 0 }))} />
                </div>
              </div>

              <div style={{ padding: '20px 28px 24px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                <button type="button" className="btn-modern btn-modern-light" onClick={() => setShowProviderModal(false)}>Cancelar</button>
                <button type="submit" className="btn-modern btn-modern-primary" disabled={!providerForm.name || !providerForm.provider_type}>
                  <FiSave size={16} className="me-1" />{editingProvider ? 'Atualizar' : 'Criar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}


    </div>
  );
}

export default IA;
